import {useEffect, useMemo, useState} from "react"
import styles from "./SaveFilterForm.module.css"
import {FilterKey, FilterList, SavedFilter} from "types/filter"
import {handleError, toastError, toastSuccess} from "helpers"
import {savedFilterService} from "services"
import {BaseButton, BaseInput} from "components"
import {Checkbox} from "antd"
import classNames from "classnames"

type Props = {
    filterKey: FilterKey
    filters: FilterList
    currentSavedFilter?: SavedFilter
    onValidateFilters: () => boolean
    onSaved?: () => void
    onCancel?: () => void
    isCompact?: boolean
}

export function SaveFilterForm(props: Props) {
    const {filterKey, filters, currentSavedFilter, onValidateFilters, onSaved, onCancel, isCompact = false} = props
    const [isFilterSaving, setIsFilterSaving] = useState(false)
    const [filterName, setFilterName] = useState("")
    const [isPublicFilter, setIsPublicFilter] = useState(false)
    const [isInputError, setIsInputError] = useState(false)
    const [filterId, setFilterId] = useState<number | null>(null)

    useEffect(() => {
        if (currentSavedFilter) {
            setFilterName(currentSavedFilter.name)
            setIsPublicFilter(currentSavedFilter.isPublic)
            setFilterId(currentSavedFilter.filterId)
        }
    }, [currentSavedFilter])

    const hasPermissionToPublicFilter = useMemo(() => {
        return true
    }, [])

    const handleSaveFilter = async () => {
        setIsInputError(false)
        if (!filterName) {
            setIsInputError(true)
            toastError("Please input filter name")
            return
        }
        if (Object.keys(filters).length <= 0) {
            toastError("No filter selected")
            return
        }
        if (!onValidateFilters()) {
            toastError("Please check your filter before saving")
            return
        }
        try {
            setIsFilterSaving(true)
            let payload: Partial<SavedFilter> = {
                filterKey,
                name: filterName,
                isPublic: isPublicFilter,
                content: JSON.stringify(filters)
            }
            if (filterId) {
                payload.filterId = filterId
                await savedFilterService.edit(payload)
            } else {
                await savedFilterService.create(payload)
            }
            toastSuccess("Saved filter")
            onSaved?.()
        } catch (err) {
            handleError(err)
        } finally {
            setIsFilterSaving(false)
        }
    }

    return (
        <div className={classNames(styles.container, {[styles.compact]: isCompact})}>
            <BaseInput
                placeholder="type filter name"
                className={styles.inputFilterName}
                value={filterName}
                onChange={(value) => {
                    setFilterName(value)
                    setFilterId(null)
                }}
                error={isInputError}
            />
            {hasPermissionToPublicFilter && (
                <div className={styles.publicWrap}>
                    <Checkbox checked={isPublicFilter} onChange={(e) => setIsPublicFilter(e.target.checked)} />
                    <span className={styles.publicLabel}>Available for all users</span>
                </div>
            )}
            <div className="flex flex-row items-center gap-8">
                <BaseButton title="Cancel" variant="secondary" onClick={onCancel} disabled={isFilterSaving} />
                <BaseButton title="Save Filter" onClick={handleSaveFilter} loading={isFilterSaving} />
            </div>
            <div className={styles.divider} />
        </div>
    )
}
